import { useEffect, useRef, useState } from "react";
import {
  adminRequest,
  getAdminHeaders,
  toAssetUrl,
  uploadEbookCover,
  uploadEbookFile,
} from "./adminApi";
import { formatFileSize } from "../../api/ebookApi";

const STATUS_LABELS = {
  draft: "草稿",
  pending: "待审核",
  published: "已发布",
  offline: "已下架",
  rejected: "审核拒绝",
};

const emptyForm = {
  title: "",
  author: "",
  publisher: "",
  publish_year: "",
  description: "",
  category_id: "",
  tags: "",
  cover_path: "",
  file_path: "",
  file_name: "",
  file_type: "",
  file_size: 0,
  copyright_type: "authorized",
  copyright_note: "",
  copyright_confirmed: 0,
  status: "draft",
  download_permission: "member_free",
  is_featured: 0,
};

const emptyCategory = {
  name: "",
  description: "",
  sort_order: 0,
  status: 1,
};

export default function AdminEbooks() {
  const [tab, setTab] = useState("books");
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [meta, setMeta] = useState({ copyright_types: [], statuses: [] });
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [keyword, setKeyword] = useState("");
  const [appliedKeyword, setAppliedKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [coverUploading, setCoverUploading] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [categoryEditing, setCategoryEditing] = useState(null);
  const coverInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const pageSize = 20;

  const loadMeta = async () => {
    const { res, data } = await adminRequest("/api/admin/ebooks/meta", {
      headers: getAdminHeaders(),
    });
    if (res.ok) setMeta(data);
  };

  const loadStats = async () => {
    const { res, data } = await adminRequest("/api/admin/ebooks/stats", {
      headers: getAdminHeaders(),
    });
    if (res.ok) setStats(data);
  };

  const loadCategories = async () => {
    const { res, data } = await adminRequest("/api/admin/ebook-categories", {
      headers: getAdminHeaders(),
    });
    if (res.ok) setCategories(data.items || []);
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      if (statusFilter) params.set("status", statusFilter);
      if (categoryFilter) params.set("category_id", categoryFilter);
      if (appliedKeyword) params.set("keyword", appliedKeyword);
      const { res, data } = await adminRequest(`/api/admin/ebooks?${params.toString()}`, {
        headers: getAdminHeaders(),
      });
      if (res.ok) {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
    loadStats();
    loadCategories();
  }, []);

  useEffect(() => {
    if (tab === "books") loadItems();
  }, [tab, page, statusFilter, categoryFilter, appliedKeyword]);

  const openCreate = () => {
    setCreating(true);
    setEditing(null);
    setForm({
      ...emptyForm,
      category_id: categories[0]?.id ? String(categories[0].id) : "",
    });
  };

  const openEdit = async (item) => {
    const { res, data } = await adminRequest(`/api/admin/ebooks/${item.id}`, {
      headers: getAdminHeaders(),
    });
    if (!res.ok) {
      alert(data?.detail || "加载失败");
      return;
    }
    setEditing(data);
    setCreating(false);
    setForm({
      title: data.title || "",
      author: data.author || "",
      publisher: data.publisher || "",
      publish_year: data.publish_year || "",
      description: data.description || "",
      category_id: data.category_id ? String(data.category_id) : "",
      tags: data.tags || "",
      cover_path: data.cover_path || "",
      file_path: data.file_path || "",
      file_name: data.file_name || "",
      file_type: data.file_type || "",
      file_size: data.file_size || 0,
      copyright_type: data.copyright_type || "authorized",
      copyright_note: data.copyright_note || "",
      copyright_confirmed: data.copyright_confirmed ? 1 : 0,
      status: data.status || "draft",
      download_permission: data.download_permission || "member_free",
      is_featured: data.is_featured ? 1 : 0,
    });
  };

  const handleCoverUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const coverPath = await uploadEbookCover(file);
      setForm((prev) => ({ ...prev, cover_path: coverPath }));
    } catch (err) {
      alert(err.message || "封面上传失败");
    } finally {
      setCoverUploading(false);
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const maxMb = Number(meta.max_ebook_mb) || 200;
    const maxBytes = Number(meta.max_ebook_bytes) || maxMb * 1024 * 1024;
    if (file.size > maxBytes) {
      alert(
        `文件约 ${(file.size / (1024 * 1024)).toFixed(1)}MB，超过上限 ${maxMb}MB。可压缩后再传，或联系运维调大 MAX_EBOOK_SIZE_MB。`
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setFileUploading(true);
    try {
      const data = await uploadEbookFile(file);
      setForm((prev) => ({
        ...prev,
        file_path: data.file_path,
        file_name: data.file_name,
        file_type: data.file_type,
        file_size: data.file_size,
      }));
    } catch (err) {
      alert(err.message || "电子书上传失败");
    } finally {
      setFileUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.title.trim()) {
      alert("请填写书名");
      return;
    }
    if (!form.category_id) {
      alert("请选择分类");
      return;
    }
    if (!form.copyright_confirmed) {
      alert("请确认该电子书具有合法上传和传播权限");
      return;
    }
    if (!editing && (!form.cover_path || !form.file_path)) {
      alert("请上传封面和电子书文件");
      return;
    }

    const payload = {
      title: form.title.trim(),
      author: form.author,
      publisher: form.publisher,
      publish_year: form.publish_year ? Number(form.publish_year) : null,
      description: form.description,
      category_id: Number(form.category_id),
      tags: form.tags,
      cover_path: form.cover_path,
      file_path: form.file_path,
      file_name: form.file_name,
      file_type: form.file_type,
      file_size: Number(form.file_size) || 0,
      copyright_type: form.copyright_type,
      copyright_note: form.copyright_note,
      copyright_confirmed: Number(form.copyright_confirmed),
      status: form.status,
      download_permission: form.download_permission,
      is_featured: Number(form.is_featured),
    };

    const url = editing ? `/api/admin/ebooks/${editing.id}` : "/api/admin/ebooks";
    const { res, data } = await adminRequest(url, {
      method: editing ? "PATCH" : "POST",
      headers: getAdminHeaders(true),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      alert(data?.detail || "保存失败");
      return;
    }
    setEditing(null);
    setCreating(false);
    setMessage("已保存");
    loadItems();
    loadStats();
  };

  const publishItem = async (item) => {
    const { res, data } = await adminRequest(`/api/admin/ebooks/${item.id}/publish`, {
      method: "POST",
      headers: getAdminHeaders(),
    });
    if (!res.ok) {
      alert(data?.detail || "发布失败");
      return;
    }
    setMessage(`「${item.title}」已发布`);
    loadItems();
    loadStats();
  };

  const offlineItem = async (item) => {
    if (!window.confirm(`下架「${item.title}」？下架后会员将无法下载，但下载记录保留。`)) return;
    const { res, data } = await adminRequest(`/api/admin/ebooks/${item.id}`, {
      method: "DELETE",
      headers: getAdminHeaders(),
    });
    if (!res.ok) {
      alert(data?.detail || "下架失败");
      return;
    }
    setMessage(`「${item.title}」已下架`);
    loadItems();
    loadStats();
  };

  const saveCategory = async () => {
    if (!categoryForm.name.trim()) {
      alert("请填写分类名称");
      return;
    }
    const payload = {
      name: categoryForm.name.trim(),
      description: categoryForm.description || "",
      sort_order: Number(categoryForm.sort_order) || 0,
      status: Number(categoryForm.status) ? 1 : 0,
    };
    const url = categoryEditing
      ? `/api/admin/ebook-categories/${categoryEditing.id}`
      : "/api/admin/ebook-categories";
    const { res, data } = await adminRequest(url, {
      method: categoryEditing ? "PATCH" : "POST",
      headers: getAdminHeaders(true),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      alert(data?.detail || "保存分类失败");
      return;
    }
    setCategoryEditing(null);
    setCategoryForm(emptyCategory);
    setMessage("分类已保存");
    loadCategories();
  };

  const showModal = creating || editing;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">📚 电子书管理</h1>
          <p className="text-sm text-gray-500 mt-1">
            上传婚恋成长类电子书，会员免费下载。请确保版权合法。
          </p>
        </div>
        {tab === "books" ? (
          <button onClick={openCreate} className="px-4 py-2 bg-pink-500 text-white rounded-lg text-sm">
            + 上传电子书
          </button>
        ) : null}
      </div>

      {stats ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { label: "总电子书", value: stats.total_ebooks },
            { label: "已发布", value: stats.published_ebooks },
            { label: "总下载次数", value: stats.total_downloads },
            { label: "今日下载", value: stats.today_downloads },
            { label: "本月下载", value: stats.month_downloads },
          ].map((item) => (
            <div key={item.label} className="bg-white rounded-xl shadow p-4">
              <p className="text-xs text-gray-500">{item.label}</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{item.value ?? 0}</p>
            </div>
          ))}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {[
          { key: "books", label: "全部电子书" },
          { key: "categories", label: "分类管理" },
        ].map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            className={`px-4 py-2 rounded-lg text-sm ${
              tab === item.key ? "bg-pink-500 text-white" : "bg-white border text-gray-600"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {message ? (
        <div className="bg-green-50 text-green-700 text-sm px-4 py-2 rounded-lg">{message}</div>
      ) : null}

      {tab === "books" ? (
        <>
          <div className="flex flex-wrap gap-2 items-center">
            {[
              { key: "", label: "全部状态" },
              { key: "draft", label: "草稿" },
              { key: "pending", label: "待审核" },
              { key: "published", label: "已发布" },
              { key: "offline", label: "已下架" },
            ].map((item) => (
              <button
                key={item.key || "all"}
                type="button"
                onClick={() => {
                  setStatusFilter(item.key);
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-full text-xs border ${
                  statusFilter === item.key
                    ? "bg-pink-50 border-pink-400 text-pink-600"
                    : "bg-white border-gray-200 text-gray-500"
                }`}
              >
                {item.label}
              </button>
            ))}
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(1);
              }}
              className="border rounded-lg px-3 py-1.5 text-sm"
            >
              <option value="">全部分类</option>
              {categories.map((cat) => (
                <option key={cat.id} value={String(cat.id)}>
                  {cat.name}
                </option>
              ))}
            </select>
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setAppliedKeyword(keyword.trim());
                setPage(1);
              }}
            >
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索书名/作者"
                className="border rounded-lg px-3 py-1.5 text-sm"
              />
              <button type="submit" className="px-3 py-1.5 border rounded-lg text-sm">
                搜索
              </button>
            </form>
          </div>

          <div className="bg-white rounded-xl shadow overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left p-3">书名</th>
                  <th className="text-left p-3">分类</th>
                  <th className="text-left p-3">浏览</th>
                  <th className="text-left p-3">下载</th>
                  <th className="text-left p-3">状态</th>
                  <th className="text-left p-3">操作</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-gray-400">
                      加载中...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-gray-400">
                      暂无电子书
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id} className="border-t">
                      <td className="p-3">
                        <div className="font-medium">{item.title}</div>
                        <div className="text-xs text-gray-400">{item.author || "未知作者"}</div>
                      </td>
                      <td className="p-3">{item.category_name || "—"}</td>
                      <td className="p-3">{item.view_count || 0}</td>
                      <td className="p-3">{item.download_count || 0}</td>
                      <td className="p-3">{STATUS_LABELS[item.status] || item.status}</td>
                      <td className="p-3 space-x-2 whitespace-nowrap">
                        <button type="button" onClick={() => openEdit(item)} className="text-blue-600">
                          编辑
                        </button>
                        {item.status !== "published" ? (
                          <button type="button" onClick={() => publishItem(item)} className="text-emerald-600">
                            发布
                          </button>
                        ) : null}
                        {item.status !== "offline" ? (
                          <button type="button" onClick={() => offlineItem(item)} className="text-red-500">
                            下架
                          </button>
                        ) : null}
                        <a
                          href={`/ebooks/${item.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-gray-500"
                        >
                          预览
                        </a>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <div className="flex justify-center gap-3">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-4 py-2 border rounded-lg text-sm disabled:opacity-40"
              >
                上一页
              </button>
              <span className="text-sm self-center">
                {page}/{totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-4 py-2 border rounded-lg text-sm disabled:opacity-40"
              >
                下一页
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow p-5 space-y-3">
            <h2 className="font-bold text-gray-800">
              {categoryEditing ? "编辑分类" : "添加分类"}
            </h2>
            <input
              placeholder="分类名称"
              value={categoryForm.name}
              onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
            <textarea
              placeholder="分类说明（可选）"
              value={categoryForm.description}
              onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
              rows={2}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
            <input
              type="number"
              placeholder="排序（越小越靠前）"
              value={categoryForm.sort_order}
              onChange={(e) => setCategoryForm({ ...categoryForm, sort_order: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={Number(categoryForm.status) === 1}
                onChange={(e) =>
                  setCategoryForm({ ...categoryForm, status: e.target.checked ? 1 : 0 })
                }
              />
              启用
            </label>
            <div className="flex gap-2">
              <button type="button" onClick={saveCategory} className="px-4 py-2 bg-pink-500 text-white rounded-lg text-sm">
                保存分类
              </button>
              {categoryEditing ? (
                <button
                  type="button"
                  onClick={() => {
                    setCategoryEditing(null);
                    setCategoryForm(emptyCategory);
                  }}
                  className="px-4 py-2 border rounded-lg text-sm"
                >
                  取消
                </button>
              ) : null}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left p-3">名称</th>
                  <th className="text-left p-3">排序</th>
                  <th className="text-left p-3">状态</th>
                  <th className="text-left p-3">操作</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat) => (
                  <tr key={cat.id} className="border-t">
                    <td className="p-3">{cat.name}</td>
                    <td className="p-3">{cat.sort_order}</td>
                    <td className="p-3">{cat.status ? "启用" : "停用"}</td>
                    <td className="p-3">
                      <button
                        type="button"
                        className="text-blue-600"
                        onClick={() => {
                          setCategoryEditing(cat);
                          setCategoryForm({
                            name: cat.name,
                            description: cat.description || "",
                            sort_order: cat.sort_order || 0,
                            status: cat.status ? 1 : 0,
                          });
                        }}
                      >
                        编辑
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal ? (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-3">
            <h3 className="font-bold">{editing ? "编辑电子书" : "上传电子书"}</h3>

            <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-900 leading-relaxed">
              仅可上传自有版权、已获授权、公版或明确允许传播的电子书。禁止上传盗版资源。
            </div>

            <input
              placeholder="书名 *"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                placeholder="作者"
                value={form.author}
                onChange={(e) => setForm({ ...form, author: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
              <input
                placeholder="出版社"
                value={form.publisher}
                onChange={(e) => setForm({ ...form, publisher: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                type="number"
                placeholder="出版年份"
                value={form.publish_year}
                onChange={(e) => setForm({ ...form, publish_year: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
              <select
                value={form.category_id}
                onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">选择分类 *</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={String(cat.id)}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <textarea
              placeholder="简介"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={4}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
            <input
              placeholder="标签（逗号分隔，可选）"
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />

            <div className="space-y-2">
              <span className="text-sm text-gray-600">封面 *</span>
              {form.cover_path ? (
                <img
                  src={toAssetUrl(form.cover_path)}
                  alt=""
                  className="w-full max-w-[160px] h-44 object-cover rounded-lg border"
                />
              ) : (
                <p className="text-xs text-gray-400">未上传封面</p>
              )}
              <input
                ref={coverInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleCoverUpload}
              />
              <button
                type="button"
                onClick={() => coverInputRef.current?.click()}
                disabled={coverUploading}
                className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50"
              >
                {coverUploading ? "上传中..." : "上传封面"}
              </button>
            </div>

            <div className="space-y-2">
              <span className="text-sm text-gray-600">
                电子书文件 *（PDF / EPUB / TXT / MOBI，单文件最大{" "}
                {meta.max_ebook_mb || 200}MB）
              </span>
              {form.file_name ? (
                <p className="text-xs text-gray-600">
                  已选：{form.file_name} · {String(form.file_type || "").toUpperCase()} ·{" "}
                  {formatFileSize(form.file_size)}
                </p>
              ) : (
                <p className="text-xs text-gray-400">未上传文件</p>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.epub,.txt,.mobi,application/pdf"
                className="hidden"
                onChange={handleFileUpload}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={fileUploading}
                className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50"
              >
                {fileUploading ? "上传中..." : "上传电子书文件"}
              </button>
            </div>

            <select
              value={form.copyright_type}
              onChange={(e) => setForm({ ...form, copyright_type: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              {(meta.copyright_types || []).map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <textarea
              placeholder="版权说明（如授权协议编号）"
              value={form.copyright_note}
              onChange={(e) => setForm({ ...form, copyright_note: e.target.value })}
              rows={2}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
            <label className="flex items-start gap-2 text-sm bg-red-50 border border-red-100 rounded-lg p-3">
              <input
                type="checkbox"
                checked={Number(form.copyright_confirmed) === 1}
                onChange={(e) =>
                  setForm({ ...form, copyright_confirmed: e.target.checked ? 1 : 0 })
                }
                className="mt-1"
              />
              <span>我确认该电子书具有合法上传和传播权限</span>
            </label>

            <div className="grid sm:grid-cols-2 gap-3">
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                {(meta.statuses || ["draft", "published"]).map((status) => (
                  <option key={status} value={status}>
                    {STATUS_LABELS[status] || status}
                  </option>
                ))}
              </select>
              <select
                value={form.download_permission}
                onChange={(e) => setForm({ ...form, download_permission: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="member_free">注册会员免费</option>
                <option value="vip_only">VIP 专享（预留）</option>
                <option value="points">积分兑换（预留）</option>
                <option value="paid">付费购买（预留）</option>
              </select>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={Number(form.is_featured) === 1}
                onChange={(e) => setForm({ ...form, is_featured: e.target.checked ? 1 : 0 })}
              />
              首页推荐
            </label>

            <div className="flex gap-2 pt-2">
              <button type="button" onClick={save} className="px-4 py-2 bg-pink-500 text-white rounded-lg text-sm">
                保存
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  setCreating(false);
                }}
                className="px-4 py-2 border rounded-lg text-sm"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
